// BL-125: the board's two sheets for divisions. From a technician's name, "Move for today": another
// division for the day, with a reason, their last moves, and Undo. From the link under the board,
// Setup's sheet: which departments are divisions and their order, and where each technician
// usually works. Both write through the actions, which keep the record (staff_division_days).

import { useEffect, useState } from 'react';
import { RpcError } from '@flowology/client';
import { uuidv7 } from '@flowology/kernel';
import { client } from './api.ts';
import type { BoardTech } from './board-model.ts';
import {
  divisionIdsOf,
  divisionPicks,
  moveLine,
  movePick,
  moveTargets,
  movedToLine,
  type Division,
  type DivisionPick,
} from './board-divisions-model.ts';
import { Sheet } from './shared/EditSheet.tsx';
import './board-divisions.css';

interface Move {
  id: string;
  divisionId: string;
  date: string;
  reason: string | null;
  by: string | null;
  state: 'live' | 'undone' | 'replaced';
}

const why = (e: unknown) => (e instanceof RpcError ? e.message : 'That did not work. Try again.');

export function MoveForDaySheet({
  tech,
  divisions,
  date,
  isToday,
  dayText,
  onClose,
  onDone,
}: {
  tech: BoardTech;
  divisions: readonly Division[];
  date: string;
  isToday: boolean;
  /** "Tuesday, October 21" for a date */
  dayText: (date: string) => string;
  onClose: () => void;
  /** after a move or an undo: what to say, and the board re-reads */
  onDone: (said: string) => void;
}) {
  const targets = moveTargets(tech, divisions);
  const [to, setTo] = useState(targets[0]?.id ?? '');
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [moves, setMoves] = useState<Move[] | null>(null);
  const [key] = useState(() => uuidv7()); // one move, however many times Move is pressed
  useEffect(() => {
    let live = true;
    client
      .call<{ moves: Move[] }>('staff.divisionMoves', { staffId: tech.id })
      .then((r) => live && setMoves(r.moves))
      .catch(() => live && setMoves([]));
    return () => {
      live = false;
    };
  }, [tech.id]);
  const when = isToday ? 'today' : dayText(date);
  const name = (id: string) => divisions.find((d) => d.id === id)?.name ?? 'another division';
  async function move() {
    if (!to || !reason.trim()) return setError('Say where to, and why.');
    setBusy(true);
    setError(null);
    try {
      await client.call('staff.moveDivisionForDay', {
        id: key,
        staffId: tech.id,
        date,
        divisionId: to,
        reason: reason.trim(),
      });
      onDone(movedToLine(tech.name, name(to), isToday, dayText(date)));
    } catch (e) {
      setError(why(e));
      setBusy(false);
    }
  }
  async function undo() {
    if (!tech.divisionMove) return;
    setBusy(true);
    setError(null);
    try {
      await client.call('staff.undoDivisionMove', { id: tech.divisionMove.id });
      const home = divisions.find((d) => d.id === tech.homeDivisionId)?.name;
      onDone(`${tech.name} is back ${home ? `in ${home}` : 'where they usually are'} for ${when}.`);
    } catch (e) {
      setError(why(e));
      setBusy(false);
    }
  }
  return (
    <Sheet title={`Move ${tech.name} for ${when}`} onClose={onClose}>
      {tech.divisionMove && (
        <div className="div-now">
          <p>
            In {name(tech.divisionMove.divisionId)} for {when}: {tech.divisionMove.reason}
            {tech.divisionMove.by ? ` (${tech.divisionMove.by})` : ''}.
          </p>
          <button type="button" className="btn soft" disabled={busy} onClick={() => void undo()}>
            Undo the move
          </button>
        </div>
      )}
      {targets.length ? (
        <>
          <label>
            To
            <select className="field" value={to} onChange={(e) => setTo(e.target.value)}>
              {targets.map((d) => (
                <option key={d.id} value={d.id}>
                  {d.name}
                  {d.id === tech.homeDivisionId ? ' (where they usually work)' : ''}
                </option>
              ))}
            </select>
          </label>
          <label>
            Why
            <textarea
              className="field"
              rows={2}
              maxLength={500}
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder="Short in drains"
            />
          </label>
        </>
      ) : (
        <p className="muted">There’s no other division to move them to.</p>
      )}
      {error && (
        <p className="notice bad small" role="alert">
          {error}
        </p>
      )}
      <section className="div-history" aria-label="Last moves">
        <h4>Last moves</h4>
        {moves === null ? (
          <p className="muted small">Getting their moves…</p>
        ) : moves.length ? (
          <ul className="small">
            {moves.map((m) => (
              <li key={m.id} className={m.state}>
                {moveLine(m, divisions, dayText)}
              </li>
            ))}
          </ul>
        ) : (
          <p className="muted small">Never moved.</p>
        )}
      </section>
      <div className="row">
        <button type="button" className="btn soft" onClick={onClose}>
          Cancel
        </button>
        {targets.length > 0 && (
          <button
            type="button"
            className="btn primary"
            disabled={busy || !to || tech.homeDivisionId === to}
            onClick={() => void move()}
          >
            Move for {isToday ? 'today' : 'the day'}
          </button>
        )}
      </div>
      {tech.homeDivisionId === to && tech.divisionMove && (
        <p className="muted small">That’s where they usually work: undo the move instead.</p>
      )}
    </Sheet>
  );
}

export function DivisionsSheet({
  divisions,
  technicians,
  onClose,
  onSaved,
}: {
  divisions: readonly Division[];
  technicians: readonly BoardTech[];
  onClose: () => void;
  onSaved: (said: string) => void;
}) {
  const [picks, setPicks] = useState<DivisionPick[] | null>(null);
  const [homes, setHomes] = useState<Record<string, string>>(() =>
    Object.fromEntries(technicians.map((t) => [t.id, t.homeDivisionId ?? ''])),
  );
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  useEffect(() => {
    let live = true;
    client
      .call<{ businessUnits: { id: string; name: string; active: boolean }[] }>('businessUnits.list', {})
      .then((r) => live && setPicks(divisionPicks(r.businessUnits, divisions)))
      .catch((e) => live && setError(why(e)));
    return () => {
      live = false;
    };
  }, [divisions]);
  const on = (picks ?? []).filter((p) => p.on);
  async function save() {
    if (!picks) return;
    setBusy(true);
    setError(null);
    try {
      await client.call('businessUnits.setDivisions', { divisionIds: divisionIdsOf(picks) });
      const kept = new Set(on.map((p) => p.id));
      for (const t of technicians) {
        const next = homes[t.id] && kept.has(homes[t.id]!) ? homes[t.id]! : null;
        if (next !== (t.homeDivisionId ?? null))
          await client.call('staff.setHomeDivision', { staffId: t.id, divisionId: next });
      }
      onSaved(
        on.length ? `The board groups by ${on.map((p) => p.name).join(', ')}.` : 'The board isn’t grouped.',
      );
    } catch (e) {
      setError(why(e));
      setBusy(false);
    }
  }
  return (
    <Sheet title="Board divisions" wide onClose={onClose}>
      <p className="muted small">
        Tick the departments the board groups technicians by, in the order they show. Everyone else comes
        last.
      </p>
      {!picks ? (
        <p className="muted small">Getting the departments…</p>
      ) : (
        <ol className="div-picks">
          {picks.map((p, i) => (
            <li key={p.id}>
              <label className="checkrow">
                <input
                  type="checkbox"
                  checked={p.on}
                  onChange={() => setPicks(picks.map((x) => (x.id === p.id ? { ...x, on: !x.on } : x)))}
                />
                {p.name}
              </label>
              <span className="div-order">
                <button
                  type="button"
                  className="btn quiet icon"
                  disabled={i === 0}
                  aria-label={`Move ${p.name} up`}
                  onClick={() => setPicks(movePick(picks, p.id, -1))}
                >
                  ↑
                </button>
                <button
                  type="button"
                  className="btn quiet icon"
                  disabled={i === picks.length - 1}
                  aria-label={`Move ${p.name} down`}
                  onClick={() => setPicks(movePick(picks, p.id, 1))}
                >
                  ↓
                </button>
              </span>
            </li>
          ))}
        </ol>
      )}
      {on.length > 0 && technicians.length > 0 && (
        <fieldset className="div-homes">
          <legend>Where each technician usually works</legend>
          {technicians.map((t) => (
            <label key={t.id}>
              {t.name}
              <select
                className="field"
                value={homes[t.id] ?? ''}
                onChange={(e) => setHomes({ ...homes, [t.id]: e.target.value })}
              >
                <option value="">Everyone else</option>
                {on.map((p) => (
                  <option key={p.id} value={p.id}>
                    {p.name}
                  </option>
                ))}
              </select>
            </label>
          ))}
        </fieldset>
      )}
      {error && (
        <p className="notice bad small" role="alert">
          {error}
        </p>
      )}
      <div className="row">
        <button type="button" className="btn soft" onClick={onClose}>
          Cancel
        </button>
        <button type="button" className="btn primary" disabled={busy || !picks} onClick={() => void save()}>
          Save
        </button>
      </div>
    </Sheet>
  );
}
